<template>
  <div class="autocomplete-wrapper relative" ref="wrapper">
    <input
      type="text"
      class="input input-bordered w-full"
      v-model="searchQuery"
      :placeholder="placeholder"
      @input="onInput"
      @focus="showDropdown = true"
      @keydown.down.prevent="navigateDown"
      @keydown.up.prevent="navigateUp"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape="showDropdown = false"
      autocomplete="off"
    />
    <input
      type="hidden"
      :value="modelValue"
      :required="required"
    />

    <div
      v-if="showDropdown && filteredOptions.length > 0"
      class="absolute z-50 w-full mt-1 bg-neutral rounded-lg shadow-lg max-h-96 overflow-y-auto"
    >
      <div class="p-1">
        <template v-for="(group, groupIndex) in groupedOptions" :key="'group-' + groupIndex">
          <!-- Parent header (only show if it has children) -->
          <div
            v-if="group.children.length > 0"
            class="px-2 py-1.5 text-xs font-bold uppercase tracking-wider opacity-50 sticky top-0 bg-neutral border-b border-white border-opacity-5 mb-0.5"
          >
            {{ group.parentName }}
          </div>

          <!-- Parent item as selectable option (if no children) -->
          <div
            v-if="group.parentOption && group.children.length === 0"
            @mousedown.prevent="selectOption(group.parentOption)"
            class="cursor-pointer rounded-md transition-colors px-2 py-1"
            :class="getHighlightClass(group.parentOption)"
          >
            <div class="text-sm font-medium" v-html="highlightMatch(group.parentOption.name)"></div>
          </div>

          <!-- Children items -->
          <div
            v-for="child in group.children"
            :key="child.id"
            @mousedown.prevent="selectOption(child)"
            class="cursor-pointer rounded-md transition-colors pl-3 pr-2 py-1"
            :class="getHighlightClass(child)"
          >
            <div class="text-sm" v-html="highlightMatch(child.name)"></div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="showDropdown && searchQuery && filteredOptions.length === 0"
      class="absolute z-50 w-full mt-1 bg-neutral rounded-lg shadow-lg p-3 text-center text-sm opacity-60"
    >
      Nenašli sa žiadne výsledky
    </div>
  </div>
</template>

<script>
export default {
  name: 'Autocomplete',
  props: {
    modelValue: {
      type: [Number, String],
      default: null
    },
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Začnite písať...'
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      searchQuery: '',
      showDropdown: false,
      highlightedIndex: 0,
      flatOptions: []
    }
  },
  computed: {
    filteredOptions() {
      if (!this.searchQuery) {
        return this.flatOptions
      }

      const query = this.searchQuery.toLowerCase()
      return this.flatOptions.filter(option => {
        const nameMatch = option.name.toLowerCase().includes(query)
        const parentMatch = option.parentName?.toLowerCase().includes(query)
        const displayMatch = option.displayName.toLowerCase().includes(query)
        return nameMatch || parentMatch || displayMatch
      })
    },
    groupedOptions() {
      const groups = []
      let currentParent = null
      let currentGroup = null

      this.filteredOptions.forEach(option => {
        if (!option.parentName) {
          // This is a parent item
          if (currentGroup) {
            groups.push(currentGroup)
          }
          currentParent = option.name
          currentGroup = {
            parentId: option.id,
            parentName: option.name,
            parentOption: option,
            children: []
          }
        } else {
          // This is a child item
          if (!currentGroup || currentGroup.parentName !== option.parentName) {
            // New parent group
            if (currentGroup) {
              groups.push(currentGroup)
            }
            currentGroup = {
              parentId: null,
              parentName: option.parentName,
              parentOption: null,
              children: [option]
            }
          } else {
            // Add to current group
            currentGroup.children.push(option)
          }
        }
      })

      if (currentGroup) {
        groups.push(currentGroup)
      }

      return groups
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.updateSearchQuery(newVal)
      }
    },
    options: {
      immediate: true,
      handler(newOptions) {
        this.flattenOptions(newOptions)
        if (this.modelValue) {
          this.updateSearchQuery(this.modelValue)
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    updateSearchQuery(value) {
      if (value) {
        const selected = this.flatOptions.find(opt => opt.id === value)
        if (selected) {
          this.searchQuery = selected.displayName
        }
      } else {
        this.searchQuery = ''
      }
    },
    flattenOptions(options) {
      this.flatOptions = []
      if (!options || options.length === 0) return

      options.forEach(option => {
        this.flatOptions.push({
          id: option.id,
          name: option.name,
          parentName: null,
          displayName: option.name,
          originalParent: null
        })

        if (option.children && option.children.length > 0) {
          option.children.forEach(child => {
            this.flatOptions.push({
              id: child.id,
              name: child.name,
              parentName: option.name,
              displayName: `${option.name} » ${child.name}`,
              originalParent: option
            })
          })
        }
      })
    },
    onInput() {
      this.showDropdown = true
      this.highlightedIndex = 0
      if (this.modelValue) {
        this.$emit('update:modelValue', null)
      }
    },
    selectOption(option) {
      this.searchQuery = option.displayName
      this.$emit('update:modelValue', option.id)
      this.showDropdown = false
    },
    navigateDown() {
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++
        this.scrollToHighlighted()
      }
    },
    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
        this.scrollToHighlighted()
      }
    },
    selectHighlighted() {
      if (this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex])
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const dropdown = this.$refs.wrapper?.querySelector('.menu')
        const highlighted = dropdown?.children[this.highlightedIndex]
        if (highlighted) {
          highlighted.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    getHighlightClass(option) {
      const currentOption = this.filteredOptions[this.highlightedIndex]
      if (currentOption && currentOption.id === option.id) {
        return 'bg-primary bg-opacity-20'
      }
      return 'hover:bg-base-300'
    },
    highlightMatch(text) {
      if (!this.searchQuery) return text

      const regex = new RegExp(`(${this.searchQuery})`, 'gi')
      return text.replace(regex, '<mark class="bg-primary text-primary-content px-0.5 rounded">$1</mark>')
    },
    handleClickOutside(event) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(event.target)) {
        this.showDropdown = false
      }
    }
  }
}
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: hsl(var(--b1));
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: hsl(var(--b3));
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--bc) / 0.3);
}
</style>
